import React from 'react';
import { Card, CardContent } from './ui/card';
import { Award, Users, Heart, Shield } from 'lucide-react';
import { practiceInfo, practiceImages } from '../data/mock';

const About = () => {
  return (
    <section id="praxis" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Über unsere Praxis
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {practiceInfo.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Dr. med. Christina Weiß
            </h3>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              Als {practiceInfo.specialty} bin ich seit {practiceInfo.since} in der Praxis tätig 
              und seit dem {practiceInfo.owner_since} Inhaberin. Mein Fokus liegt auf einer 
              umfassenden, einfühlsamen Betreuung, die auf die individuellen Bedürfnisse jeder 
              Patientin eingeht.
            </p>
            <p className="text-gray-600 mb-8 text-lg leading-relaxed">
              Unterstützt werde ich von einem erfahrenen Team aus medizinischen Fachangestellten, 
              die mit mir zusammen für eine vertrauensvolle Atmosphäre und professionelle 
              Behandlung sorgen.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <Award className="h-5 w-5 text-red-600 mr-3" />
                <span className="text-gray-700">Fachärztin seit 1999</span>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-red-600 mr-3" />
                <span className="text-gray-700">Erfahrenes Team</span>
              </div>
              <div className="flex items-center">
                <Heart className="h-5 w-5 text-red-600 mr-3" />
                <span className="text-gray-700">Einfühlsame Betreuung</span>
              </div>
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-red-600 mr-3" />
                <span className="text-gray-700">Modernste Diagnostik</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-red-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Patientenorientiert</h4>
                <p className="text-gray-600 text-sm">
                  Individuelle Betreuung mit Zeit und Aufmerksamkeit für jede Patientin
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-red-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Moderne Ausstattung</h4>
                <p className="text-gray-600 text-sm">
                  Drei moderne Behandlungsräume mit neuester medizinischer Technik
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-red-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Langjährige Erfahrung</h4>
                <p className="text-gray-600 text-sm">
                  Über 15 Jahre Expertise in Gynäkologie und Geburtshilfe
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-red-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Kompetentes Team</h4>
                <p className="text-gray-600 text-sm">
                  Qualifizierte medizinische Fachangestellte mit jahrelanger Erfahrung
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Practice Interior Photos */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Unsere modernen Praxisräume
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Erleben Sie eine professionelle und gleichzeitig warme Atmosphäre in unseren 
              drei modernen Behandlungsräumen.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {practiceImages.map((image) => (
              <Card key={image.id} className="bg-white hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-red-200 group overflow-hidden">
                <CardContent className="p-0">
                  <div className="aspect-w-4 aspect-h-3 overflow-hidden">
                    <img 
                      src={image.image} 
                      alt={image.title}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-red-700 transition-colors duration-300">
                      {image.title}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {image.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;